/**
 * useGetCustomerId is a custom hook for retrieving the current customer's Id.
 *
 * @returns {Object} - An object containing the current customer's ID.
 *                    - customerId: The ID of the current customer (undefined if not loaded).
 *
 */

import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { RootState } from "src/store";

export const useGetCustomerId = () => {
  const selector = useCallback(
    (state: RootState) => ({
      currentCustomer: state.generalReducer.applicationReducer.currentCustomer
    }),
    []
  );

  const { currentCustomer } = useSelector(selector, (prev, cur) => prev.currentCustomer === cur.currentCustomer);

  const getCustomerId = useMemo(() => (currentCustomer.loading === "has-loaded" ? currentCustomer.data.customerId : undefined), [currentCustomer]);
  return { customerId: getCustomerId };
};

/**
 * useGetCustomerDetails is a custom hook that retrieves the customer's all details .
 *   @property {Object|undefined} customerDetails - The customer's data when successfully loaded (`loading === "has-loaded"`),
 *                                                or `undefined` if still loading or failed.
 */

export const useGetCustomerDetails = () => {
  const selector = useCallback(
    (state: RootState) => ({
      customerDetails: state.generalReducer.applicationReducer.customerDetails
    }),
    []
  );

  const { customerDetails } = useSelector(selector, (prev, cur) => prev.customerDetails === cur.customerDetails);

  const getCustomerDetails = useMemo(() => (customerDetails.loading === "has-loaded" ? customerDetails.data : undefined), [customerDetails]);
  return { customerDetails: getCustomerDetails };
};
